import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import TradingViewWidget from '@/components/TradingViewWidget';
import { getIndicatorInfo } from '@/content/indicators';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

// Custom Pine Script indicators (open in TradingView directly)
const customIndicators = [
  { id: 'p0Od8Ad6', name: 'Indicator 1' },
  { id: '1W2O5GAX', name: 'Indicator 2' },
  { id: '6iX7xuP0', name: 'Indicator 3' },
  { id: '6KKpt6U4', name: 'Indicator 4' },
];

// Standard indicators
const standardIndicators = [
  { id: 'rsi', name: 'RSI', study: 'STD;RSI' },
  { id: 'macd', name: 'MACD', study: 'STD;MACD' },
  { id: 'ema', name: 'EMA', study: 'STD;EMA%1' },
  { id: 'volume', name: 'Volume', study: 'STD;Volume' },
];

const Chart = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || 'BINANCE:BTCUSDT');
  const [tf, setTf] = useState(searchParams.get('tf') || '60');
  const [indicator, setIndicator] = useState(searchParams.get('indicator') || '');
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);

  // Update URL when params change
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (symbol) newParams.set('symbol', symbol);
    if (tf) newParams.set('tf', tf);
    if (indicator) newParams.set('indicator', indicator);
    setSearchParams(newParams, { replace: true });
  }, [symbol, tf, indicator, setSearchParams]);

  // Map timeframes for display
  const timeframeMap: Record<string, string> = {
    '60': '1h',
    '240': '4h', 
    '1D': '1D',
    'D': '1D',
    '1W': '1W',
    'W': '1W'
  };

  const getDisplayTimeframe = (tfValue: string): string => {
    return timeframeMap[tfValue] || tfValue;
  };

  // Convert TradingView interval format
  const getTradingViewInterval = (tfValue: string): string => {
    const intervalMap: Record<string, string> = {
      '60': '60',
      '240': '240', 
      '1D': '1D',
      'D': '1D',
      '1W': '1W',
      'W': '1W'
    };
    return intervalMap[tfValue] || '60';
  };

  const handleTimeframeChange = (newTf: string) => {
    setTf(newTf);
  };

  const indicatorInfo = getIndicatorInfo(indicator);

  const handleIndicatorToggle = (indicatorId: string) => {
    setSelectedIndicators(prev => 
      prev.includes(indicatorId) 
        ? prev.filter(id => id !== indicatorId)
        : [...prev, indicatorId]
    );
  };

  const getStudies = () => {
    const studies: string[] = [];
    selectedIndicators.forEach(id => {
      const standardIndicator = standardIndicators.find(ind => ind.id === id);
      if (standardIndicator) studies.push(standardIndicator.study);
    });
    return studies;
  };

  // Generate TradingView URL for custom indicators
  const getTradingViewUrl = (indicatorId: string) => {
    const tvSymbol = symbol.replace(':', '%3A');
    return `https://www.tradingview.com/chart/?symbol=${tvSymbol}&interval=${getTradingViewInterval(tf)}&solution=${indicatorId}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Trading Chart
                </h1>
                <p className="text-muted-foreground">
                  {symbol} {getDisplayTimeframe(tf)} - {indicatorInfo.title}
                </p>
              </div>
            </div>
            
            {/* Timeframe buttons */}
            <div className="flex gap-2">
              {['60', '240', '1D'].map((tfOption) => (
                <Button
                  key={tfOption}
                  variant={tf === tfOption ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleTimeframeChange(tfOption)}
                  className="text-xs"
                >
                  {getDisplayTimeframe(tfOption)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          {/* Indicators Section */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Indicadores Pine Script</h3>
              <Badge variant="outline" className="text-xs">
                <ExternalLink className="h-3 w-3 mr-1" />
                Abre no TradingView
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {customIndicators.map((ind) => (
                <Button
                  key={ind.id}
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1"
                  asChild
                >
                  <a 
                    href={getTradingViewUrl(ind.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {ind.name}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </Button>
              ))}
            </div>
            
            <h4 className="text-md font-medium mt-4">Indicadores Padrão</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {standardIndicators.map((indicator) => (
                <Button
                  key={indicator.id}
                  variant={selectedIndicators.includes(indicator.id) ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => handleIndicatorToggle(indicator.id)}
                  className="text-xs"
                >
                  {indicator.name}
                </Button>
              ))}
            </div>

            {selectedIndicators.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                <span className="text-sm text-muted-foreground">Ativos:</span>
                {selectedIndicators.map(id => {
                  const ind = standardIndicators.find(i => i.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="text-xs">
                      {ind?.name}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* TradingView Widget */}
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="w-full" style={{ height: '600px' }}>
                <TradingViewWidget
                  symbol={symbol}
                  interval={getTradingViewInterval(tf)}
                  theme="dark"
                  autosize={false}
                  width="100%"
                  height="600"
                  locale="pt"
                  timezone="America/Sao_Paulo"
                  style="1"
                  enable_publishing={false}
                  withdateranges={true}
                  hide_side_toolbar={false}
                  allow_symbol_change={true}
                  details={true}
                  hotlist={false}
                  calendar={false}
                  studies={getStudies()}
                />
              </div>
            </CardContent>
          </Card>

          {/* Indicator Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📊 {indicatorInfo.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none dark:prose-invert">
                {indicatorInfo.description.map((paragraph, index) => (
                  <p key={index} className="mb-3 leading-relaxed text-muted-foreground">
                    {paragraph.split('**').map((part, partIndex) => 
                      partIndex % 2 === 1 ? (
                        <strong key={partIndex} className="text-foreground font-semibold">
                          {part}
                        </strong>
                      ) : (
                        part
                      )
                    )}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-sm">💡 Como usar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>• Use os botões de timeframe (1h, 4h, 1D) para alternar períodos</p>
                <p>• O símbolo pode ser alterado diretamente no gráfico do TradingView</p>
                <p>• Para receber alertas via Telegram, configure seus indicadores no TradingView</p>
                <p>• URL format: <code className="bg-muted px-1 rounded">/chart?symbol=BINANCE:BTCUSDT&tf=60&indicator=GlobalLiquidityAlpha</code></p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Chart;